// External Dependencies
import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Icons
import { IoArrowBackOutline, IoClose, IoLink, IoBusiness } from 'react-icons/io5'
import { FiUpload, FiChevronDown } from 'react-icons/fi'

// Custom Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'
import configManager from '../../config/configManager'

// Constants
const MATERIAL_CATEGORIES = [
  { value: 'Stone', label: 'Stone' },
  { value: 'Brick', label: 'Brick' },
  { value: 'Wood', label: 'Wood' },
  { value: 'Terrazzo', label: 'Terrazzo' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Metal', label: 'Metal' },
  { value: 'Tile', label: 'Tile' },
  { value: 'Fabric', label: 'Fabric' },
  { value: 'Wallpaper', label: 'Wallpaper' },
  { value: 'Carpet', label: 'Carpet' },
  { value: 'Surfaces', label: 'Surfaces' },
  { value: 'Finishes', label: 'Finishes' },
  { value: 'Landscaping', label: 'Landscaping' },
  { value: 'Insulation', label: 'Insulation' },
  { value: 'Organic', label: 'Organic' },
  { value: 'Acoustic', label: 'Acoustic' },
]

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const PAGE_SIZE = 20

// Custom Multi-Select Dropdown Component
const MultiSelect = ({
  label,
  options,
  value = [],
  onChange,
  error,
  placeholder,
  loading,
  hasMore,
  onLoadMore,
  disabled = false,
  required = false,
  getOptionLabel,
  getOptionValue,
  loadingText = 'Loading more...',
  noOptionsText = 'No options available',
  loadMoreText = 'Load More',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const listRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter options based on search
  const filteredOptions = searchTerm
    ? options.filter(opt =>
      getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle scroll for infinite loading
  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight * 0.8 && !loading && hasMore) {
      onLoadMore?.()
    }
  }

  const handleToggleOption = (option) => {
    const optionValue = getOptionValue(option)
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const removeOption = (optionValue) => {
    onChange(value.filter(v => v !== optionValue))
  }

  const selectedOptions = options.filter(opt => value.includes(getOptionValue(opt)))

  return (
    <div className='w-full' ref={dropdownRef}>
      {label && (
        <label className='block text-sm font-medium mb-2'>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
      )}
      <div className='relative'>
        {/* Trigger Button */}
        <button
          type='button'
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full border-[2px] ${error ? 'border-red-500' : 'border-light-gray'} 
            rounded-lg p-3 focus:outline-none focus:border-prime 
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
            flex items-center justify-between text-left min-h-[52px]`}
        >
          <div className='flex flex-wrap gap-1.5 flex-1'>
            {selectedOptions.length > 0 ? (
              selectedOptions.map(opt => (
                <span
                  key={getOptionValue(opt)}
                  className='inline-flex items-center gap-1 bg-prime/10 text-prime px-2 py-1 rounded-md text-sm'
                >
                  {getOptionLabel(opt)}
                  {!disabled && (
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation()
                        removeOption(getOptionValue(opt))
                      }}
                      className='hover:text-red-500'
                    >
                      <IoClose size={14} />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className='text-gray-400'>{placeholder || 'Select...'}</span>
            )}
          </div>
          <FiChevronDown className={`transition-transform ml-2 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown List */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-1 bg-white border border-light-gray rounded-lg shadow-lg overflow-hidden'>
            {/* Search Input */}
            <div className='p-2 border-b border-light-gray'>
              <input
                type='text'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full border rounded-lg p-2 text-sm focus:outline-none focus:border-prime'
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options List */}
            <ul
              ref={listRef}
              onScroll={handleListScroll}
              className='max-h-60 overflow-y-auto'
            >
              {filteredOptions.length === 0 ? (
                <li className='px-4 py-2 text-gray-500 text-sm text-center'>
                  {searchTerm ? 'No matching options' : noOptionsText}
                </li>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option)
                  const isSelected = value.includes(optionValue)
                  return (
                    <li
                      key={optionValue || index}
                      onClick={() => handleToggleOption(option)}
                      className={`px-4 py-2 text-sm cursor-pointer hover:bg-prime/10 transition-colors flex items-center gap-2
                        ${isSelected ? 'bg-prime/10 text-prime' : 'text-gray-700'}`}
                    >
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => { }}
                        className='w-4 h-4 text-prime border-gray-300 rounded focus:ring-prime'
                      />
                      {getOptionLabel(option)}
                    </li>
                  )
                })
              )}

              {/* Load More Button */}
              {hasMore && !loading && filteredOptions.length > 0 && (
                <li>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      onLoadMore?.()
                    }}
                    className='w-full px-4 py-2 text-sm text-prime hover:bg-prime/5 transition-colors border-t border-light-gray font-medium'
                  >
                    {loadMoreText}
                  </button>
                </li>
              )}

              {/* Loading Indicator */}
              {loading && (
                <li className='px-4 py-2 text-gray-400 text-sm text-center flex items-center justify-center gap-2'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-prime'></div>
                  {loadingText}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
    </div>
  )
}

const MaterialAdd = () => {
  // Hooks
  const navigate = useNavigate()
  const GoBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []
  const fileInputRef = useRef(null)
  const configData = configManager()

  // State Management
  const [brands, setBrands] = useState([])
  const [patterns, setPatterns] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  // Pagination states for Patterns
  const [patternPage, setPatternPage] = useState(1)
  const [patternHasMore, setPatternHasMore] = useState(true)
  const [patternLoading, setPatternLoading] = useState(false)

  const [formData, setFormData] = useState({
    materialName: '',
    materialDescription: '',
    selectedCategory: 'Select Category',
    selectedBrand: '',
    selectedPatterns: [], // Array of pattern IDs (numbers)
    materialPreview: null,
    materialFile: null,
    realwidth_mm: '0',
    pixelwidth: '',
    realheight: '',
    // Auto-filled brand fields
    brands_name: '',
    brands_website_link: '',
    brands_logo: '',
  })
  const [errors, setErrors] = useState({})

  // ---------- Fetch Patterns with Pagination ----------
  const fetchPatterns = async (page = 1, append = true) => {
    if (patternLoading) return
    setPatternLoading(true)
    try {
      const response = await axiosInterceptorInstance.post(`/patterns?page=${page}&limit=${PAGE_SIZE}&front=false`, {
        front: false
      })
      const results = response.data?.data?.results || response.data?.results || []
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1

      if (append) {
        // Use 'id' field for deduplication
        const existingIds = new Set(patterns.map(item => item.id))
        const uniqueNew = results.filter(item => !existingIds.has(item.id))
        setPatterns(prev => [...prev, ...uniqueNew])
      } else {
        setPatterns(results)
      }

      setPatternHasMore(page < totalPages)
      setPatternPage(page)
    } catch (err) {
      CustomToast({ message: 'Failed to load patterns.', type: 'error' })
    } finally {
      setPatternLoading(false)
    }
  }

  // Load brands and patterns on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axiosInterceptorInstance.post('/brands', {
          page: 1,
          limit: 200
        })
        const brandsData = response.data?.data?.results || response.data?.results || []
        setBrands(brandsData)
      } catch (err) {
        CustomToast({ message: 'Failed to load brands.', type: 'error' })
      }
    }
    fetchBrands()
    fetchPatterns(1, false)
  }, [])

  // Load more patterns
  const loadMorePatterns = () => {
    if (!patternLoading && patternHasMore) {
      fetchPatterns(patternPage + 1, true)
    }
  }

  // Form Validation
  const validateInputs = () => {
    const newErrors = {}

    if (!formData.materialName.trim()) {
      newErrors.materialName = 'Material name is required.'
    }

    if (!formData.materialDescription.trim()) {
      newErrors.materialDescription = 'Material description is required.'
    }

    if (formData.selectedCategory === 'Select Category') {
      newErrors.category = 'Please select a valid category.'
    }

    if (!formData.materialFile) {
      newErrors.file = 'Please upload a material file.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle Brand Change - Auto-fill brand fields
  const handleBrandChange = (e) => {
    const brandId = e.target.value
    const selectedBrand = brands.find(b => b._id === brandId)

    if (selectedBrand) {
      setFormData((prev) => ({
        ...prev,
        selectedBrand: brandId,
        brands_name: selectedBrand.name || '',
        brands_website_link: selectedBrand.website_link || '',
        brands_logo: selectedBrand.logo || '',
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedBrand: '',
        brands_name: '',
        brands_website_link: '',
        brands_logo: '',
      }))
    }
    setErrors((prev) => ({ ...prev, brand: null }))
  }

  // Handle manual brand name update
  const handleBrandNameChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      brands_name: e.target.value,
    }))
  }

  // Handle manual brand website update
  const handleBrandWebsiteChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      brands_website_link: e.target.value,
    }))
  }

  // ---------- Handle Patterns Change - FIX: Convert to Numbers ----------
  const handlePatternsChange = (selectedPatterns) => {
    // Convert all selected pattern IDs to numbers
    const patternIdsAsNumbers = selectedPatterns.map(id => Number(id))

    setFormData((prev) => ({
      ...prev,
      selectedPatterns: patternIdsAsNumbers, // Store as numbers [1, 3, 7]
    }))
    setErrors((prev) => ({ ...prev, patterns: null }))
  }

  // File Handling
  const handleImageChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: 'Invalid file type. Only JPG, PNG, and JPEG are accepted.',
      }))
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        file: 'File size must be less than 5MB.',
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      materialFile: file,
      materialPreview: URL.createObjectURL(file),
    }))
    setErrors((prev) => ({ ...prev, file: null }))
  }

  const handleDelete = () => {
    setFormData((prev) => ({
      ...prev,
      materialFile: null,
      materialPreview: null,
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // ---------- Form Submission - FIX: Send Numbers ----------
  // ---------- Form Submission - Send as Individual Fields ----------
  const handleSubmit = async () => {
    if (!userPermissions.includes('material:create')) {
      CustomToast({
        message: 'You do not have permission to create materials.',
        type: 'error',
      })
      return
    }

    if (!validateInputs()) return

    setIsUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.materialName)
      formDataToSend.append('description', formData.materialDescription)
      formDataToSend.append('category', formData.selectedCategory)
      formDataToSend.append('file', formData.materialFile)
      formDataToSend.append('type', 'Material')
      formDataToSend.append('realwidth_mm', formData.realwidth_mm || '2000')
      formDataToSend.append('pixelwidth', formData.pixelwidth || '0')
      formDataToSend.append('realheight', formData.realheight || '0')

      // ---------- Patterns - Send as Individual Fields ----------
      if (formData.selectedPatterns && formData.selectedPatterns.length > 0) {
        // Send each pattern as individual field: patterns[0], patterns[1], etc.
        formData.selectedPatterns.forEach((patternId, index) => {
          formDataToSend.append(`patterns[${index}]`, patternId)
        })
      }

      // Brand fields
      if (formData.selectedBrand) {
        formDataToSend.append('brands_name', formData.brands_name || '3d')
        formDataToSend.append('brands_website_link', formData.brands_website_link || '')
        formDataToSend.append('brands_logo', formData.brands_logo || '')
        formDataToSend.append('brand_id', formData.selectedBrand)
      } else {
        formDataToSend.append('brands_name', formData.brands_name || '3d')
        formDataToSend.append('brands_website_link', formData.brands_website_link || '')
        formDataToSend.append('brands_logo', formData.brands_logo || '')
        formDataToSend.append('brand_id', '')
      }

      await axiosInterceptorInstance.post('/materials/upload', formDataToSend)

      CustomToast({
        message: 'Material uploaded successfully.',
        type: 'success',
      })
      navigate('/material')
    } catch (error) {
      CustomToast({
        message:
          error.response?.data?.message ||
          'Failed to upload material. Please try again!',
        type: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  // Helper function to get brand logo URL
  const getBrandLogoUrl = (logo) => {
    if (!logo) return null
    if (logo.startsWith('/')) {
      return `${configData.CDN_BASE_URL}${logo}`
    }
    if (logo.startsWith('http')) {
      return logo
    }
    return `${configData.CDN_BASE_URL}/brands/${logo}`
  }

  // Helper functions for MultiSelect
  const getPatternLabel = (p) => p.name || p.id || p._id
  const getPatternValue = (p) => {
    // Return as number
    const val = p.id || p._id
    return Number(val)
  }

  const brandLogoUrl = getBrandLogoUrl(formData.brands_logo)

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Add Material</h1>
          </div>
        </div>
      </PageHeader>

      <div className='space-y-6 flex flex-col items-center pb-16'>
        {/* Category Selection */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='category'>
            Select Category:
          </label>
          <select
            id='category'
            className={`w-full border-[2px] ${errors.category ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.selectedCategory}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                selectedCategory: e.target.value,
              }))
              setErrors((prev) => ({ ...prev, category: null }))
            }}
          >
            <option disabled value='Select Category'>
              Select Category
            </option>
            {MATERIAL_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className='text-red-500 text-sm mt-1'>{errors.category}</p>
          )}
        </div>

        {/* Brand Selection - Optional */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='brand'>
            Select Brand: <span className='text-xs text-gray-400 font-normal'>(Optional)</span>
          </label>
          <select
            id='brand'
            className={`w-full border-[2px] ${errors.brand ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.selectedBrand}
            onChange={handleBrandChange}
          >
            <option value=''>Select Brand (Optional)</option>
            {brands.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name || brand._id}
              </option>
            ))}
          </select>
          {errors.brand && (
            <p className='text-red-500 text-sm mt-1'>{errors.brand}</p>
          )}
        </div>

        {/* Brand Details Display */}
        {(formData.brands_name || formData.brands_website_link || brandLogoUrl) && (
          <div className='w-full sm:w-[500px] p-4 bg-gray-50 border border-gray-200 rounded-lg'>
            <h3 className='text-sm font-medium text-gray-700 mb-3'>Brand Details</h3>

            <div className='space-y-3'>
              {/* Brand Logo */}
              {brandLogoUrl && (
                <div className='flex items-center gap-3'>
                  <span className='text-sm text-gray-500 w-24'>Logo:</span>
                  <img
                    src={brandLogoUrl}
                    alt={formData.brands_name || 'Brand logo'}
                    className='h-10 object-contain border border-gray-200 rounded p-1 bg-white'
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
              )}

              {/* Brand Name - Editable */}
              <div className='flex items-center gap-3'>
                <span className='text-sm text-gray-500 w-24 flex items-center gap-1'>
                  <IoBusiness className='text-gray-400' />
                  Name:
                </span>
                <input
                  type='text'
                  value={formData.brands_name}
                  onChange={handleBrandNameChange}
                  placeholder='Brand name'
                  className='flex-1 border-[2px] border-light-gray rounded-lg p-2 focus:outline-none focus:border-prime text-sm'
                />
              </div>

              {/* Brand Website - Editable */}
              <div className='flex items-center gap-3'>
                <span className='text-sm text-gray-500 w-24 flex items-center gap-1'>
                  <IoLink className='text-gray-400' />
                  Website:
                </span>
                <input
                  type='url'
                  value={formData.brands_website_link}
                  onChange={handleBrandWebsiteChange}
                  placeholder='https://example.com'
                  className='flex-1 border-[2px] border-light-gray rounded-lg p-2 focus:outline-none focus:border-prime text-sm'
                />
                {formData.brands_website_link && (
                  <a
                    href={formData.brands_website_link}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-prime hover:underline text-sm whitespace-nowrap'
                  >
                    Visit
                  </a>
                )}
              </div>

              <div className='text-xs text-gray-400 mt-2'>
                <p>💡 Brand name and website can be updated. Logo is read-only.</p>
              </div>
            </div>
          </div>
        )}

        {/* Material Name Input */}
        <div className='w-full sm:w-[500px]'>
          <label
            className='block text-sm font-medium mb-2'
            htmlFor='materialName'
          >
            Material Name:
          </label>
          <input
            id='materialName'
            type='text'
            placeholder='Material Name'
            className={`w-full border-[2px] ${errors.materialName ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.materialName}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, materialName: e.target.value }))
              setErrors((prev) => ({ ...prev, materialName: null }))
            }}
          />
          {errors.materialName && (
            <p className='text-red-500 text-sm mt-1'>{errors.materialName}</p>
          )}
        </div>

        {/* Material Description */}
        <div className='w-full sm:w-[500px]'>
          <label
            className='block text-sm font-medium mb-2'
            htmlFor='materialDescription'
          >
            Material Description:
          </label>
          <textarea
            id='materialDescription'
            className={`w-full border-[2px] ${errors.materialDescription
              ? 'border-red-500'
              : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.materialDescription}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                materialDescription: e.target.value,
              }))
              setErrors((prev) => ({ ...prev, materialDescription: null }))
            }}
            rows={4}
          />
          {errors.materialDescription && (
            <p className='text-red-500 text-sm mt-1'>
              {errors.materialDescription}
            </p>
          )}
        </div>

        {/* Patterns Selection - Multi Select */}
        <div className='w-full sm:w-[500px]'>
          <MultiSelect
            label='Select Patterns:'
            options={patterns}
            value={formData.selectedPatterns}
            onChange={handlePatternsChange}
            placeholder='Search and select patterns...'
            loading={patternLoading}
            hasMore={patternHasMore}
            onLoadMore={loadMorePatterns}
            getOptionLabel={getPatternLabel}
            getOptionValue={getPatternValue}
            loadingText='Loading more patterns...'
            loadMoreText='Load More Patterns'
            noOptionsText='No patterns available'
          />
        </div>

        {/* Tile Physical Width */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='realwidth_mm'>
            Tile Physical Width (mm):
          </label>
          <input
            id='realwidth_mm'
            type='number'
            min='1'
            max='99999'
            placeholder='e.g. 2000'
            className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime'
            value={formData.realwidth_mm}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, realwidth_mm: e.target.value }))
            }
          />
          <div className='mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-1.5'>
            <p className='font-semibold text-gray-700'>
              💡 How to Set the Physical Width
            </p>
            <ul className='list-disc pl-4 space-y-1'>
              <li>
                <strong>Single Tile / Plank:</strong> If the image contains only one tile or plank, enter its actual physical width (e.g., <strong>300 mm</strong> or <strong>600 mm</strong>).
              </li>
              <li>
                <strong>Seamless Pattern:</strong> If the image shows multiple repeating tiles or bricks, enter the total width covered by the visible pattern (e.g., <strong>1000–1500 mm</strong>).
              </li>
              <li>
                <strong>Large Surfaces (Concrete, Grass, Asphalt):</strong> For large seamless textures, use a standard physical width between <strong>2500–4000 mm</strong> (e.g., <strong>3000 mm</strong>).
              </li>
            </ul>
          </div>
        </div>

        {/* File Upload Section */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>
            Upload Material:
          </label>
          <div className='relative'>
            <div
              className={`relative w-full aspect-[9/5] rounded-lg bg-primary bg-opacity-5 cursor-pointer border ${errors.file ? 'border-red-500' : 'border-dashed border-prime'
                } flex items-center justify-center`}
            >
              {!formData.materialPreview && (
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  className='absolute inset-0 opacity-0 cursor-pointer'
                  onChange={handleImageChange}
                  aria-label='Upload material image'
                />
              )}
              {formData.materialPreview ? (
                <img
                  src={formData.materialPreview}
                  alt='Material preview'
                  className='absolute top-0 left-0 object-cover w-full h-full rounded-lg'
                />
              ) : (
                <div className='flex flex-col gap-4 items-center'>
                  <FiUpload className='text-4xl text-white bg-prime rounded-full cursor-pointer p-2' />
                  <span className='text-prime'>
                    Only JPG, PNG, and JPEG files are accepted
                  </span>
                </div>
              )}
              {formData.materialFile && (
                <button
                  type='button'
                  onClick={handleDelete}
                  className='absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-prime bg-opacity-80 rounded-full text-primary bg-white'
                  aria-label='Remove uploaded image'
                >
                  <IoClose />
                </button>
              )}
            </div>
            {errors.file && (
              <p className='text-red-500 text-sm mt-1'>{errors.file}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className='w-full sm:w-[500px] py-3 rounded-md text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-opacity-80 disabled:text-white'
        >
          {isUploading ? 'Uploading...' : 'Upload Material'}
        </button>
      </div>
    </div>
  )
}

export default MaterialAdd